import React, { PropsWithChildren, useState } from "react";
import { styled } from "../../stitches.config";
import CloseIcon from "../assets/icons/close-icon.svg";
import Button from "../atoms/Button";
import CategoriesTabs from "../CategoriesTabs";
import { h3 } from "../commonStyles/Fonts";

export enum DashboardModalType {
  CATEGORY = "Kategorie",
  TAGS = "Tagi",
  AUTHOR = "Autorzy",
  MAIN_SETTINGS = "Wybierz treści, które chcesz widzieć na stronie głównej",
}

interface IDashboardModal {
  type: DashboardModalType;
  onCloseBtn: () => void;
  onRemoveIgnores?: () => void;
  onSave?: () => void;
}

const DashboardModal: React.FC<PropsWithChildren<IDashboardModal>> = ({
  type,
  onCloseBtn,
  children,
  onRemoveIgnores,
  onSave,
}) => {
  const isGrid =
    type === DashboardModalType.AUTHOR ||
    type === DashboardModalType.MAIN_SETTINGS;
  return (
    <Mask>
      <Wrapper>
        <ModalHeader>
          <h3 className={h3()}>{type}</h3>
          <CloseButton onClick={onCloseBtn}>
            <img src={CloseIcon} />
          </CloseButton>
        </ModalHeader>
        <ModalContent className={isGrid ? "grid" : undefined}>
          {children}
        </ModalContent>
        <ModalFooter>
          {type === DashboardModalType.MAIN_SETTINGS && (
            <ActionButton onClick={onRemoveIgnores}>
              Włącz ponownie wszystkie kategorie
            </ActionButton>
          )}
          <BtnsWrapper>
            <Button
              type="secondary"
              text="Anuluj"
              starPosition="none"
              onClick={onCloseBtn}
            />
            <Button
              text="Zapisz"
              starPosition="none"
              onClick={() => {
                onSave && onSave();
                onCloseBtn();
              }}
            />
          </BtnsWrapper>
        </ModalFooter>
      </Wrapper>
    </Mask>
  );
};

const Wrapper = styled("div", {
  width: "100%",
  height: "100%",
  backgroundColor: "$grayscale0",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  "@bp4": {
    width: "80%",
    height: "80%",
    padding: "0 32px",
  },
});

const Mask = styled("div", {
  position: "fixed",
  top: 0,
  left: 0,
  zIndex: 10000000,
  width: "100vw",
  height: "100vh",
  backgroundColor: "rgba(19, 18, 18,0.75)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "none",
});

const ModalHeader = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const ModalContent = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  gap: "16px",
  overflow: "scroll",
  maxHeight: "calc(100% - 134px)",
  marginBottom: "auto",
  "&.grid": {
    display: "grid",
    width: "100%",
    gridTemplateColumns: "1fr",
    "& > div": {
      width: "100%",
    },
    "@bp4": {
      gridTemplateColumns: "1fr 1fr 1fr",
    },
  },
});

const ModalFooter = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexDirection: "column",
  "@bp4": {
    padding: "16px 0",
    justifyContent: "flex-end",
    gap: "24px",
    flexDirection: "row",
  },
});

const BtnsWrapper = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  "@bp4": {
    padding: "16px 0",
    justifyContent: "flex-end",
    gap: "24px",
  },
});

const CloseButton = styled("button", {
  border: "none",
  background: "none",
  "& img": {
    filter: "invert(1)",
    width: "16px",
  },
});

const CategoryWrapper = styled("div", {
  paddingBottom: "8px",
});

const ActionButton = styled("button", {
  color: "$primary",
  background: "none",
  borderWidth: "0px",
  cursor: "pointer",
  marginRight: "auto",
  paddingBottom: "12px",
  whiteSpace: "nowrap",
});

export default DashboardModal;
